/* ===================================================================
   useIntegrationRequest() -- mutation for the onboarding "suggest your
   software" form (Brad's form UI).

   Wraps submitIntegrationRequest() (src/lib/supabase.ts), which inserts
   directly via the anon Supabase client. client_id is auto-scoped by the
   DB default (public.current_client_id()), so the form does not need it.

   Usage in Brad's form:
     const submit = useIntegrationRequest();
     submit.mutate({ softwareName, businessCategory, note, submittedByEmail });
     // submit.isPending / submit.isSuccess / submit.error drive the UI.
   =================================================================== */
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import {
  submitIntegrationRequest,
  type IntegrationRequestInput,
  type IntegrationRequestResult,
} from '../lib/supabase';

export function useIntegrationRequest(): UseMutationResult<
  IntegrationRequestResult,
  Error,
  IntegrationRequestInput
> {
  return useMutation({
    mutationFn: async (input: IntegrationRequestInput) => {
      const res = await submitIntegrationRequest(input);
      // Normalize a soft failure (RLS / validation) into a thrown error so
      // mutation.error / onError work the way React Query callers expect.
      if (!res.ok) throw new Error(res.error ?? 'Could not submit your request.');
      return res;
    },
  });
}
